import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";

const STATUS_TONE = {
  delivered: "text-emerald-600", paid: "text-emerald-600",
  sent_to_accounting: "text-purple-600", invoiced: "text-purple-600",
  dispatched: "text-[#0A84FF]", in_transit: "text-[#0A84FF]",
};

export const TopLoadSearch = () => {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState(null);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  const search = async () => {
    const digits = q.replace(/\D/g, "");
    if (!digits) { toast.error("Type a load number, e.g. MFG-0126"); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/loads/find-by-number", { params: { number: digits } });
      if (!data.length) { toast.error(`No load found for MFG-${digits.padStart(4, "0")}`); setHits(null); return; }
      if (data.length === 1) { open(data[0]); return; }
      setHits(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setBusy(false); }
  };

  const open = (l) => {
    setQ(""); setHits(null);
    nav(`/portal/loads/${l.id}`);
  };

  return (
    <div className="relative mb-4 md:mb-6" ref={boxRef} data-testid="top-load-search">
      <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-xl shadow-sm px-3 py-1.5 md:py-2 focus-within:border-[#0A84FF] max-w-md">
        <MagnifyingGlass size={16} weight="bold" className="text-neutral-400 shrink-0" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); if (hits) setHits(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") search(); if (e.key === "Escape") { setQ(""); setHits(null); } }}
          placeholder="Jump to load — MFG-####"
          inputMode="numeric"
          disabled={busy}
          data-testid="top-load-search-input"
          className="flex-1 text-sm outline-none bg-transparent placeholder-neutral-400"
        />
        <button onClick={search} disabled={busy} data-testid="top-load-search-go"
                className="shrink-0 w-9 h-9 md:w-7 md:h-7 !min-h-0 grid place-items-center rounded-lg bg-black text-white hover:bg-neutral-800 disabled:opacity-40">
          <ArrowRight size={14} weight="bold" />
        </button>
      </div>
      {hits && hits.length > 1 && (
        <div className="absolute z-40 mt-1 w-full max-w-md bg-white border border-neutral-200 rounded-xl shadow-xl overflow-hidden"
             data-testid="top-load-search-results">
          {hits.map((l) => (
            <button key={l.id} onClick={() => open(l)} data-testid={`top-load-hit-${l.id}`}
                    className="w-full text-left px-3 py-2.5 hover:bg-[#0A84FF]/5 border-b border-neutral-100 last:border-0">
              <span className="font-mono font-bold text-[#0A84FF] text-sm">MFG-{l.load_number}</span>
              <span className="text-sm text-neutral-700 ml-2">{l.lane}</span>
              <span className={`text-xs ml-2 font-semibold ${STATUS_TONE[l.status] || "text-neutral-400"}`}>{l.status}</span>
              {l.carrier_name && <span className="text-xs text-neutral-400 ml-2">· {l.carrier_name}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopLoadSearch;
